import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import MoveIcon from '@cartwhl/ui/icons/MoveIcon';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';

import {
  readBonePoseBaselineTransform,
  readBonePoseTransform,
  type AxisTriplet,
  type BonePoseTransform,
} from '../../../lib/skeleton/boneTransforms';
import type { Bone } from '../../../lib/skeleton/core';
import { getBoneDccFacingEulerOrderLabel } from '../../../lib/fbx/rotateOrderAdapter';
import ResetToDefaultButton from '../../../presentations/ResetToDefaultButton';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import {
  INSPECTOR_ROTATE_DRAG_DELTA_PER_PIXEL,
  INSPECTOR_TRANSLATE_DRAG_DELTA_PER_PIXEL,
} from '../constants';
import AxisTripletFields from './AxisTripletFields';
import Section from './Section';

interface PoseAxisInputsRowProps {
  label: string;
  labelIcon: ReactNode;
  compact: boolean;
  values: AxisTriplet;
  baselineValues: AxisTriplet;
  onAxisChange: (axisIndex: number, nextValue: number) => void;
  dragDeltaPerPixel: number;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
  onDragValueChangeStart?: () => void;
}

interface PoseTransformEditorProps {
  bone: Bone;
  boneIndex: number;
  poseVersion?: number;
  compactLayout?: boolean;
  onChange?: (boneIndex: number, transform: BonePoseTransform) => void;
  onReset?: (boneIndex: number) => void;
  onTranslateValueChangeStart?: () => void;
  onTranslateValueChangeEnd?: () => void;
  onTranslateDragValueChangeStart?: () => void;
  onRotateValueChangeStart?: () => void;
  onRotateValueChangeEnd?: () => void;
  onRotateDragValueChangeStart?: () => void;
}

const COMPACT_LAYOUT_THRESHOLD_PX = 430;

function isPoseLocalIdentity(bone: Bone): boolean {
  const elements = bone.poseLocal.elements;
  const identity = [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
  ];

  for (let i = 0; i < identity.length; i += 1) {
    if (Math.abs(elements[i] - identity[i]) > 1e-6) return false;
  }
  return true;
}

function PoseAxisInputsRow({
  label,
  labelIcon,
  compact,
  values,
  baselineValues,
  onAxisChange,
  dragDeltaPerPixel,
  onValueChangeStart,
  onValueChangeEnd,
  onDragValueChangeStart,
}: PoseAxisInputsRowProps) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className={`${compact ? 'w-6' : 'w-16'} shrink-0`}>
        {compact ? (
          <Tooltip text={label} side="top" inModal>
            <span className="border-gray-30/80 dark:border-gray-60/80 bg-white/45 dark:bg-gray-90/45 text-primary inline-flex h-5 w-5 items-center justify-center rounded-md border">
              {labelIcon}
            </span>
          </Tooltip>
        ) : (
          <Text
            variant="studio/body/medium/xs"
            className={`${PANEL_WRITING_CLASSNAME} block truncate`}
          >
            {label}
          </Text>
        )}
      </div>
      <AxisTripletFields
        values={values}
        baselineValues={baselineValues}
        editable
        ariaLabelPrefix={label}
        onAxisChange={onAxisChange}
        dragDeltaPerPixel={dragDeltaPerPixel}
        onValueChangeStart={onValueChangeStart}
        onValueChangeEnd={onValueChangeEnd}
        onDragValueChangeStart={onDragValueChangeStart}
      />
      <span
        aria-label="Local space"
        className="border-gray-30/60 dark:border-gray-60/60 bg-white/20 dark:bg-gray-90/20 inline-flex h-5 min-w-5 shrink-0 cursor-default select-none items-center justify-center rounded-md border px-1 tabular-nums"
      >
        <Text as="span" variant="studio/body/medium/xs" className="text-secondary">
          L
        </Text>
      </span>
    </div>
  );
}

export default function PoseTransformEditor({
  bone,
  boneIndex,
  compactLayout,
  onChange,
  onReset,
  onTranslateValueChangeStart,
  onTranslateValueChangeEnd,
  onTranslateDragValueChangeStart,
  onRotateValueChangeStart,
  onRotateValueChangeEnd,
  onRotateDragValueChangeStart,
}: PoseTransformEditorProps) {
  const rowContentRef = useRef<HTMLDivElement>(null);
  const [detectedCompactLayout, setDetectedCompactLayout] = useState(false);
  const resolvedCompactLayout = compactLayout ?? detectedCompactLayout;

  useLayoutEffect(() => {
    if (compactLayout !== undefined) return;
    const element = rowContentRef.current;
    if (!element) return;

    const updateCompactState = () => {
      setDetectedCompactLayout(
        element.getBoundingClientRect().width < COMPACT_LAYOUT_THRESHOLD_PX,
      );
    };
    updateCompactState();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => updateCompactState());
    observer.observe(element);
    return () => observer.disconnect();
  }, [compactLayout]);

  const poseTransform = readBonePoseTransform(bone);
  const poseBaselineTransform = readBonePoseBaselineTransform(bone);
  const isDefaultPose = isPoseLocalIdentity(bone);
  const rotateOrderLabel = getBoneDccFacingEulerOrderLabel(bone);

  const handleTranslateAxisChange = useCallback(
    (axisIndex: number, nextValue: number) => {
      const nextTranslate = [...poseTransform.translate] as AxisTriplet;
      const nextRotate = [...poseTransform.rotate] as AxisTriplet;
      nextTranslate[axisIndex] = nextValue;
      onChange?.(boneIndex, {
        translate: nextTranslate,
        rotate: nextRotate,
      });
    },
    [boneIndex, onChange, poseTransform],
  );

  const handleRotateAxisChange = useCallback(
    (axisIndex: number, nextValue: number) => {
      const nextTranslate = [...poseTransform.translate] as AxisTriplet;
      const nextRotate = [...poseTransform.rotate] as AxisTriplet;
      nextRotate[axisIndex] = nextValue;
      onChange?.(boneIndex, {
        translate: nextTranslate,
        rotate: nextRotate,
      });
    },
    [boneIndex, onChange, poseTransform],
  );

  return (
    <Section
      title="Pose"
      headerActions={
        <div className="flex items-center gap-1.5">
          <ResetToDefaultButton
            label="Pose transform"
            tooltipText="Reset pose transform to rest pose"
            isDefault={isDefaultPose}
            onReset={() => onReset?.(boneIndex)}
          />
          <Tooltip
            text={`Rotate order (DCC): ${rotateOrderLabel}`}
            side="top"
            inModal
          >
            <span
              aria-label={`Rotate order ${rotateOrderLabel}`}
              className="border-gray-30/80 dark:border-gray-60/80 bg-white inline-flex h-5 min-w-5 shrink-0 cursor-default select-none items-center justify-center rounded-full border px-1 tabular-nums"
            >
              <Text
                as="span"
                variant="studio/body/xs"
                className="text-primary"
                style={{ fontSize: '10px', lineHeight: '10px' }}
              >
                {rotateOrderLabel}
              </Text>
            </span>
          </Tooltip>
        </div>
      }
    >
      <div ref={rowContentRef} className="space-y-0.5">
        <PoseAxisInputsRow
          label="Translate"
          labelIcon={<MoveIcon className="h-3 w-3" />}
          compact={resolvedCompactLayout}
          values={poseTransform.translate}
          baselineValues={poseBaselineTransform.translate}
          onAxisChange={handleTranslateAxisChange}
          dragDeltaPerPixel={INSPECTOR_TRANSLATE_DRAG_DELTA_PER_PIXEL}
          onValueChangeStart={onTranslateValueChangeStart}
          onValueChangeEnd={onTranslateValueChangeEnd}
          onDragValueChangeStart={onTranslateDragValueChangeStart}
        />
        <PoseAxisInputsRow
          label="Rotate"
          labelIcon={<RotateIcon className="h-3 w-3" />}
          compact={resolvedCompactLayout}
          values={poseTransform.rotate}
          baselineValues={poseBaselineTransform.rotate}
          onAxisChange={handleRotateAxisChange}
          dragDeltaPerPixel={INSPECTOR_ROTATE_DRAG_DELTA_PER_PIXEL}
          onValueChangeStart={onRotateValueChangeStart}
          onValueChangeEnd={onRotateValueChangeEnd}
          onDragValueChangeStart={onRotateDragValueChangeStart}
        />
      </div>
    </Section>
  );
}
